import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { PermissionEnum } from '../enum/permission-enum';
import { NavItemModel } from '../models/nav-item-model';
import { UserLoggedIn } from '../models/user-login.model';
import { AuthGuard } from './auth-guard.service';
import { CurrentUserService } from './current-user.service';
import { AiAdminService } from 'src/app/modules/ai-assistant/services/ai-admin.service';
import { UmRole } from 'src/app/modules/roles/models/role-enum';

/** Translation key of the Administration parent in the navigation list. */
const ADMINISTRATION_MENU_KEY = 'menu.administration';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  currentUser: UserLoggedIn | null = null;

  private maxRetries = 10; // Define max retry attempts
  private retryCount = 0; // Track retry attempts

  /**
   * Cached answer for AiAssistant.ManageConfiguration, together with the user it was resolved
   * for. This service is a root singleton and logging out is an in-app navigation rather than a
   * page reload, so a cache that was not keyed on the user would survive a sign-out and hand the
   * next account the previous one's verdict — which is how a user holding only AiAssistant.Use
   * ended up seeing the configuration entry.
   */
  private aiConfigPrivilege: Promise<boolean> | null = null;
  private aiConfigPrivilegeUserId: string | null = null;

  constructor(
    private authGuard: AuthGuard,
    private currentUserService: CurrentUserService,
    private aiAdminService: AiAdminService
  ) {}

  filterNavItems(navItems: NavItemModel[]): NavItemModel[] {
    return navItems.filter(item => {
      // Check if the item has permissions and if the user has access

      if (
        item.data?.permissionCodes &&
        !this.authGuard.HasPermission(item.data.permissionCodes)
      ) {
        return false;
      }

      // Hide the item if the user has any explicitly excluded permission
      if (
        item.data?.excludedPermissionCodes &&
        this.authGuard.HasPermission(item.data.excludedPermissionCodes)
      ) {
        return false;
      }

      // Hide the item from explicitly excluded roles (permissions can be shared across roles)
      if (
        item.data?.excludedRoleIds &&
        item.data.excludedRoleIds.includes(
          this.currentUserService.getCurrentUserRole()
        )
      ) {
        return false;
      }

      // Recursively filter child items

      if (item.childs && item.childs.length > 0) {
        item.childs = this.filterNavItems(item.childs);
      }

      return true;
    });
  }

  // getNavItems(): Observable<NavItemModel[]> {
  //   return new Observable(sub => {
  //     this.navList.push(
  //       {
  //         order: 2,
  //         name: 'menu.tracker',
  //         title: 'menu.tracker',
  //         icon: '',
  //         svg: 'tracker',
  //         cssClass: '',
  //         link: '/tracker',
  //         childs: [],
  //         data: { permissionCodes: [PermissionEnum.ProjectTaskList] },
  //       },
  //       {
  //         order: 3,
  //         name: 'menu.sections',
  //         title: 'menu.sections',
  //         icon: '',
  //         svg: 'sections',
  //         cssClass: '',
  //         link: '/section',
  //         childs: [],
  //         data: { permissionCodes: [PermissionEnum.SectionList] },
  //       },
  //       {
  //         order: 4,
  //         name: 'menu.projects',
  //         title: 'menu.projects',
  //         icon: '',
  //         svg: 'timeline',
  //         cssClass: '',
  //         link: '/project/list',
  //         childs: [],
  //         data: { permissionCodes: [PermissionEnum.ProjectList] },
  //       },
  //       {
  //         order: 5,
  //         name: 'menu.Lookups',
  //         title: 'menu.Lookups',
  //         icon: '',
  //         svg: 'settings',
  //         cssClass: '',
  //         childs: [
  //           {
  //             order: 1,
  //             link: `/pmo`,
  //             name: 'menu.pmo',
  //             title: 'menu.pmo',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.PMOList] },

  //           },
  //           {
  //             order: 2,
  //             link: `/persona`,
  //             name: 'menu.persona',
  //             title: 'menu.persona',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.PersonaList] },
  //           },
  //           {
  //             order: 3,
  //             link: `/sourceMarket`,
  //             name: 'menu.sourceMarket',
  //             title: 'menu.sourceMarket',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.SourceMarketList] },

  //           },
  //           {
  //             order: 4,
  //             link: `/stratigeic-objective`,
  //             name: 'menu.stratigeicObjective',
  //             title: 'menu.stratigeicObjective',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.StrategicObjectiveList] },

  //           },

  //           {
  //             order: 5,
  //             link: `/stackholder-department`,
  //             name: 'menu.stackholder-department',
  //             title: 'menu.stackholder-department',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.StackholderDepartmentList] },

  //           },
  //           {
  //             order: 6,
  //             link: `/stackholder-member`,
  //             name: 'menu.stackholder-member',
  //             title: 'menu.stackholder-member',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.StackholderMemberList] },

  //           },
  //           {
  //             order: 7,
  //             link: `/consultation-company`,
  //             name: 'menu.consultation-company',
  //             title: 'menu.consultation-company',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.ConsultationCompanyList] },

  //           },
  //           {
  //             order: 8,
  //             link: `/responsible-department`,
  //             name: 'menu.responsible-department',
  //             title: 'menu.responsible-department',
  //             icon: '',
  //             img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
  //             cssClass: '',
  //             childs: [],
  //             data: { permissionCodes: [PermissionEnum.ResponsibleDepartmentList] },

  //           }
  //         ],
  //         data: {
  //           permissionCodes: [
  //             PermissionEnum.PMOList,
  //             PermissionEnum.PersonaList,
  //             PermissionEnum.SourceMarketList,
  //             PermissionEnum.StrategicObjectiveList,
  //             PermissionEnum.StackholderDepartmentList,
  //             PermissionEnum.StackholderMemberList,
  //             PermissionEnum.ConsultationCompanyList
  //           ],
  //         },
  //       },
  //       {
  //         order: 6,
  //         name: 'menu.reports',
  //         title: 'menu.reports',
  //         icon: '',
  //         svg: 'timeline',
  //         cssClass: '',
  //         link: '/reports/list',
  //         childs: [],
  //         data: { permissionCodes: [PermissionEnum.ReportList] },
  //       },
  //     );

  //     // Filter the navigation list based on permissions
  //     const filteredNavList = this.filterNavItems(this.navList);

  //     sub.next(filteredNavList);
  //   });
  // }

  getNavItems(): Observable<NavItemModel[]> {
    return new Observable(sub => {
      firstValueFrom(this.currentUserService.isTokenExchangeCompleted()).then(
        isExchanged => {
          if (!isExchanged) {
            if (this.retryCount < this.maxRetries) {
              this.retryCount++;
              setTimeout(() => {
                this.getNavItems().subscribe(sub); // Retry with a limit
              }, 100);
            } else {
              console.error('Max retries reached for token exchange.');
              sub.complete();
            }
            return;
          }

          // Reset retry count after success
          this.retryCount = 0;

          // Define the complete navigation list
          const allNavItems: NavItemModel[] = [
            {
              order: 1,
              name: 'menu.dashboard',
              title: 'menu.dashboard',
              icon: '',
              img: 'assets/images/dashboard.png',
              cssClass: '',
              link: '/dashboard',
              childs: [],
              data: { permissionCodes: [PermissionEnum.ProjectTaskList] },
            },
            {
              order: 2,
              name: 'menu.sections',
              title: 'menu.sections',
              icon: '',
              svg: 'sections',
              cssClass: '',
              link: '/section',
              childs: [],
              data: { permissionCodes: [PermissionEnum.SectionList] },
            },
            {
              order: 3,
              name: 'menu.projects',
              title: 'menu.projects',
              icon: '',
              svg: 'timeline',
              cssClass: '',
              link: '/project/list',
              childs: [],
              data: {
                permissionCodes: [
                  PermissionEnum.ProjectList,
                  PermissionEnum.MileStoneList,
                ],
              },
            },
            {
              order: 4,
              name: 'menu.tasks',
              title: 'menu.tasks',
              icon: '',
              svg: 'tasks',
              cssClass: '',
              link: '/tasks',
              childs: [],
              data: { permissionCodes: [PermissionEnum.ProjectTaskList] },
            },
            {
              order: 5,
              name: 'cr.changeRequests',
              title: 'menu.cr',
              icon: '',
              svg: 'timeline',
              cssClass: '',
              link: '/project-cr/all',
              childs: [],
              data: { permissionCodes: [PermissionEnum.ChangeRequestList] },
            },
            {
              order: 6,
              name: 'menu.Lookups',
              title: 'menu.Lookups',
              icon: '',
              svg: 'settings',
              cssClass: '',
              childs: [
                {
                  order: 9,
                  link: `/tags`,
                  name: 'menu.tags',
                  title: 'menu.tags',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/lookupIcon.svg',
                  cssClass: '',
                  childs: [],
                  // data: { permissionCodes: [PermissionEnum.ResponsibleDepartmentList] },
                },
              ],
              data: {
                permissionCodes: [PermissionEnum.ProjectTaskAdd],
              },
            },
            {
              name: 'menu.msmDashboard',
              link: undefined, // Parent menu is not clickable
              img: 'assets/images/dashboard.png',
              title: 'menu.msmDashboard',
              childs: [
                {
                  name: 'menu.overallAnalysis',
                  link: '/msm-checklist/general-dashboard/divisions',
                  childs: [],
                  title: 'menu.divisionsDashboard',
                  order: 1,
                  cssClass: '',
                },

                {
                  name: 'menu.agentsPerformance',
                  link: '/msm-checklist/general-dashboard/agent-performance',
                  childs: [],
                  title: 'menu.agentPerformanceDashboard',
                  order: 2,
                  cssClass: '',
                  data: { permissionCodes: [PermissionEnum.Phead] },
                },

                {
                  name: 'menu.sectionsScores',
                  link: '/msm-checklist/general-dashboard/sections-dashboard',
                  childs: [],
                  title: 'menu.sectionsScores',
                  order: 3,
                  cssClass: '',
                  data: { permissionCodes: [PermissionEnum.Phead] },
                },

                // ,{
                //   name: 'Hospitality Dashboard',
                //   link: '/msm-checklist/general-dashboard/hospitality',
                //   childs: [],
                //   title: 'Hospitality Dashboard',
                //   order: 2,
                //   cssClass: ''
                // }
              ],
              cssClass: '',
              expanded: false,
              data: {
                permissionCodes: [
                  PermissionEnum.Phead,
                  PermissionEnum.DAdmin,
                  PermissionEnum.GetDashBoardItemsScore,
                ],
                excludedPermissionCodes: [PermissionEnum.PAdmin],
              },

              order: 9, // Adjust order to place it appropriately
            },
            {
              order: 9,
              name: 'menu.schedule',
              title: 'menu.schedule',
              icon: '',
              img: 'assets/images/schedule.png',
              cssClass: '',
              link: '/msm-checklist/schedule/list',
              childs: [],
              data: {
                permissionCodes: [PermissionEnum.Phead],
              },
            },
            {
              order: 10,
              name: 'menu.reviewChecklist',
              title: 'menu.reviewChecklist',
              icon: '',
              img: 'assets/images/checklists_per_q.png',
              cssClass: '',
              link: '/msm-checklist/department-admin/checklist-review-list',
              childs: [],
              data: {
                permissionCodes: [PermissionEnum.DAdmin],
                excludedRoleIds: [UmRole.Stakeholder],
              },
            },

            {
              order: 10,
              name: 'menu.checklistUI',
              title: 'menu.checklistUI',
              icon: '',
              img: 'assets/images/checklists.png',
              cssClass: '',
              childs: [
                {
                  order: 1,
                  link: `/msm-checklist/checklist-ui/add`,
                  name: 'menu.checklistAdd',
                  title: 'menu.checklistAdd',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/timeline.svg',
                  cssClass: '',
                  childs: [],
                  data: { permissionCodes: [PermissionEnum.Phead] },
                },
                {
                  order: 2,
                  link: `/msm-checklist/checklist-ui`,
                  name: 'menu.checklistList',
                  title: 'menu.checklistList',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/timeline.svg',
                  cssClass: '',
                  childs: [],
                  data: {
                    permissionCodes: [
                      PermissionEnum.Phead,
                      PermissionEnum.AgentDashboard,
                    ],
                  },
                },
              ],
              data: {
                permissionCodes: [
                  PermissionEnum.Phead,
                  PermissionEnum.AgentDashboard,
                ],
              },
            },
            {
              order: 11,
              link: '/project-head-ui/schedule-review-list',
              name: 'menu.scheduleReviewList',
              title: 'menu.scheduleReviewList',
              icon: '',
              img: 'assets/images/checklists_per_q.png',

              cssClass: '',
              childs: [],
              data: {
                permissionCodes: [PermissionEnum.Phead, PermissionEnum.PAdmin],
              },
            },
            {
              order: 12,
              name: 'menu.checklistPerQuestion',
              title: 'menu.checklistPerQuestion',
              icon: '',
              img: 'assets/images/checklists.png',
              cssClass: '',
              link: '/msm-checklist/department-admin/checklist-per-question',
              childs: [],
              data: {
                permissionCodes: [PermissionEnum.DAdmin, PermissionEnum.Phead],
              },
            },
            {
              order: 13,
              name: 'menu.myDashboard',
              title: 'menu.agentDashboard',
              icon: '',
              img: 'assets/images/dashboard.png',
              cssClass: '',
              link: '/msm-checklist/agent-ui/dashboard',
              childs: [],
              data: { permissionCodes: [PermissionEnum.AgentDashboard] },
            },
            {
              order: 14,
              name: 'menu.mySchedule',
              link: '/msm-checklist/agent-ui/schedule',
              img: 'assets/images/schedule.png',
              childs: [],
              title: 'menu.agentSchedules',
              cssClass: '',
              data: { permissionCodes: [PermissionEnum.AgentDashboard] },
            },
            {
              order: 14.5,
              name: 'menu.myTimeSheet',
              link: '/msm-checklist/agent-ui/my-timesheet',
              img: 'assets/images/schedule.png',
              childs: [],
              title: 'menu.myTimeSheet',
              cssClass: '',
              data: {
                permissionCodes: [
                  PermissionEnum.AgentDashboard,
                  PermissionEnum.PAdmin,
                ],
              },
            },

            {
              order: 15,
              name: 'menu.actionPlan',
              title: 'menu.actionPlan',
              icon: '',
              img: 'assets/images/action-plan.png',
              cssClass: '',
              childs: [
                // {
                //   order: 1,
                //   link: `/msm-checklist/mystery-shopper`,
                //   name: 'menu.mysteryShopperLanding',
                //   title: 'menu.mysteryShopperLanding',
                //   icon: '',
                //   img: '../../../../../../assets/media/sidebar/eye.svg',
                //   cssClass: '',
                //   childs: [],
                //   // data: { permissionCodes: [PermissionEnum.MSMCheckList] },
                // },
                {
                  order: 2,
                  link: `/msm-checklist/action-plan/schedule`,
                  name: 'menu.scheduleWithMissingActionPlan',
                  title: 'menu.scheduleWithMissingActionPlan',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/timeline.svg',
                  cssClass: '',
                  childs: [],
                  data: {
                    permissionCodes: [
                      PermissionEnum.Phead,
                      PermissionEnum.PAdmin,
                    ],
                  },
                },
                {
                  order: 3,
                  link: `/msm-checklist/action-plan/list`,
                  name: 'menu.actionPlanList',
                  title: 'menu.actionPlanList',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/timeline.svg',
                  cssClass: '',
                  childs: [],
                  data: { permissionCodes: [PermissionEnum.MSMCheckList] },
                },
                {
                  order: 4,
                  link: `/msm-checklist/action-plan/item-master`,
                  name: 'menu.actionPlanItems',
                  title: 'menu.actionPlanItems',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/timeline.svg',
                  cssClass: '',
                  childs: [],
                  data: { permissionCodes: [PermissionEnum.MSMCheckList] },
                },
                {
                  order: 5,
                  link: `/msm-checklist/action-plan/dashboard`,
                  name: 'menu.actionPlanDashboard',
                  title: 'menu.actionPlanDashboard',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/dashboard.svg',
                  cssClass: '',
                  childs: [],
                  data: { permissionCodes: [PermissionEnum.Phead] },
                },
              ],
              data: {
                permissionCodes: [PermissionEnum.MSMCheckList],
              },
            },
            {
              name: 'menu.adminDashboard',
              link: undefined, // Parent menu is not clickable
              img: 'assets/images/dashboard.png',
              title: 'menu.adminDashboard',
              childs: [
                {
                  name: 'menu.statistics',
                  link: '/msm-checklist/project-admin-ui',
                  childs: [],
                  title: 'menu.projectAdminDashboard',
                  order: 1,
                  cssClass: '',
                },
                {
                  name: 'menu.analysis',
                  link: '/msm-checklist/general-dashboard/divisions',
                  childs: [],
                  title: 'menu.divisionsDashboard',
                  order: 2,
                  cssClass: '',
                },
                // , {
                //   name: 'Tasks',
                //   link: '/msm-checklist/project-admin-ui/tasks',
                //   childs: [],
                //   title: 'Project Admin Tasks',
                //   order: 2,
                //   cssClass: ''
                // },
                // {
                //   name: 'All Tasks',
                //   link: '/msm-checklist/project-admin-ui/all-tasks',
                //   svg: 'tasks',
                //   childs: [],
                //   title: 'All Tasks',
                //   order: 3,
                //   cssClass: ''
                // },
              ],
              cssClass: '',
              expanded: false,
              data: { permissionCodes: [PermissionEnum.PAdmin] },

              order: 16, // Adjust order to place it appropriately
            },
            {
              order: 16,
              name: 'menu.administration',
              link: undefined,
              img: 'assets/images/systemcode.png',
              title: 'menu.administration',
              cssClass: '',
              childs: [
                {
                  name: 'menu.assignStakeholders',
                  link: '/msm-checklist/configuration/stakeholders',
                  childs: [],
                  title: 'menu.assignStakeholdersTitle',
                  order: 1,
                  cssClass: '',
                },
                {
                  name: 'menu.assignUserChecklists',
                  link: '/msm-checklist/configuration/assign-user-checklists',
                  childs: [],
                  title: 'menu.assignUserChecklistsTitle',
                  order: 2,
                  cssClass: '',
                },
                {
                  name: 'menu.hierarchyToCategory',
                  link: '/msm-checklist/configuration/hierarchy-to-category',
                  childs: [],
                  title: 'menu.hierarchyToCategoryTitle',
                  order: 3,
                  cssClass: '',
                },
                {
                  name: 'menu.emailTemplatesMenu',
                  link: '/msm-checklist/configuration/email-templates',
                  childs: [],
                  title: 'menu.emailTemplatesMenuTitle',
                  order: 4,
                  cssClass: '',
                },
                {
                  order: 4,
                  link: `/system-code`,
                  name: 'menu.systemCodes',
                  title: 'menu.systemCodes',
                  cssClass: '',
                  childs: [],
                  data: { permissionCodes: [PermissionEnum.SystemCode] },
                },
                {
                  order: 4,
                  link: `/user-management`,
                  name: 'menu.usersList',
                  title: 'menu.usersList',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/users.svg',
                  cssClass: '',
                  childs: [],
                  data: { permissionCodes: [PermissionEnum.UserManagement] },
                },
                {
                  order: 5,
                  link: `/user-management/roles/list`,
                  name: 'menu.rolesList',
                  title: 'menu.rolesList',
                  icon: '',
                  img: '../../../../../../assets/media/sidebar/roles.svg',
                  cssClass: '',
                  childs: [],
                  data: { permissionCodes: [PermissionEnum.UserManagement] },
                },
              ],
              data: { permissionCodes: [PermissionEnum.Phead] },
            },

            //{
            //  order: 16, // Adjust order to place it appropriately, icon_configuration.png
            //  name: 'System Setting',
            //  link: undefined, // Parent menu is not clickable
            //  img: 'assets/images/icon_configuration.png',

            //  title: 'System Setting',
            //  cssClass: '',
            //  childs: [
            //    {
            //      name: 'Assign Stakeholders',
            //      link: '/msm-checklist/configuration/stakeholders',
            //      childs: [],
            //      title: 'Assign Stakeholders',
            //      order: 1,
            //      cssClass: ''
            //    },
            //    {
            //      name: 'Hierarchy to Category',
            //      link: '/msm-checklist/configuration/hierarchy-to-category',
            //      childs: [],
            //      title: 'Hierarchy to Category',
            //      order: 2,
            //      cssClass: ''
            //    }
            //  ],
            //  data: { permissionCodes: [PermissionEnum.Phead] },
            //},
            //{
            //  order: 17,
            //  link: `/msm-checklist/checklist-ui/closed-checklists`,
            //  name: 'menu.closedChecklists',
            //  title: 'menu.closedChecklists',
            //  icon: '',
            //  img: 'assets/images/checklists.png',

            //  cssClass: '',
            //  childs: [],
            //  data: { permissionCodes: [PermissionEnum.MSMCheckList] },
            //},
            // {
            //   order: 15,
            //   name: 'menu.projectHeadUI',
            //   title: 'menu.projectHeadUI',
            //   icon: '',
            //   svg: 'tasks',
            //   cssClass: '',
            //   childs: [

            //     {
            //       order: 2,
            //       link: '/project-head-ui/project-head-tasks',
            //       name: 'menu.projectHeadTasks',
            //       title: 'menu.projectHeadTasks',
            //       icon: '',
            //       img: '../../../../../../assets/media/sidebar/tasks.svg',
            //       cssClass: '',
            //       childs: [],
            //       data: { permissionCodes: [PermissionEnum.MSMCheckList] },
            //     },
            //     {
            //       order: 3,
            //       link: '/msm-checklist/schedule/reschedule-list',
            //       name: 'menu.rescheduleList',
            //       title: 'menu.rescheduleList',
            //       icon: '',
            //       img: '../../../../../../assets/media/sidebar/calendar.svg',
            //       cssClass: '',
            //       childs: [],
            //       data: { permissionCodes: [PermissionEnum.MSMCheckList] },
            //     }
            //   ],
            //   data: { permissionCodes: [PermissionEnum.MSMCheckList] },
            // },reports1.png
            {
              order: 20,
              name: 'menu.closedChecklists',
              title: 'menu.closedChecklists',
              icon: '',
              img: 'assets/images/checklists.png',

              cssClass: '',
              link: '/msm-checklist/checklist-ui/closed-checklists',
              childs: [],
              data: {
                permissionCodes: [PermissionEnum.MSMCheckList],
                excludedRoleIds: [UmRole.Stakeholder],
              },
            },
            // Hidden until image search ships; uncomment to restore the menu entry.
            // {
            //   // Project Head only while image tagging is in pilot. The API checks the role too,
            //   // so this only decides who sees the entry.
            //   order: 20,
            //   name: 'menu.imageSearch',
            //   title: 'menu.imageSearch',
            //   icon: '',
            //   img: 'assets/images/checklists.png',
            //   cssClass: '',
            //   link: '/ai-assistant/images',
            //   childs: [],
            //   data: { permissionCodes: [PermissionEnum.Phead] },
            // },
            {
              order: 20,
              name: 'menu.reports',
              title: 'menu.reports',
              icon: '',
              img: 'assets/images/reports1.png',

              cssClass: '',
              link: undefined,
              childs: [
                {
                  name: 'menu.reportsList',
                  link: '/reports/list',
                  childs: [],
                  title: 'menu.reportsList',
                  order: 1,
                  cssClass: '',
                  data: { permissionCodes: [PermissionEnum.ReportList] },
                },
                {
                  name: 'menu.templateListView',
                  link: '/msm-checklist/templates-ui/templates-list-view',
                  childs: [],
                  title: 'menu.templateListView',
                  order: 2,
                  cssClass: '',
                  data: {
                    permissionCodes: [
                      // PermissionEnum.DAdmin,
                      PermissionEnum.PAdmin,
                      PermissionEnum.Phead,
                    ],
                  },
                },
                {
                  name: 'menu.uploadNewTemplates',
                  link: '/msm-checklist/templates-ui/upload-new-template',
                  childs: [],
                  title: 'menu.uploadNewTemplates',
                  order: 3,
                  cssClass: '',
                  data: {
                    permissionCodes: [
                      //PermissionEnum.DAdmin,
                      PermissionEnum.PAdmin,
                      PermissionEnum.Phead,
                    ],
                  },
                },
                {
                  name: 'menu.generateReport',
                  link: '/msm-checklist/templates-ui/generate-report',
                  childs: [],
                  title: 'menu.generateReport',
                  order: 4,
                  cssClass: '',
                  data: {
                    permissionCodes: [
                      PermissionEnum.Phead,
                      PermissionEnum.PAdmin,
                    ],
                  },
                },
                {
                  name: 'menu.serviceExcellence',
                  link: '/msm-checklist/service-excellence-report',
                  childs: [],
                  title: 'menu.serviceExcellence',
                  order: 5,
                  cssClass: '',
                  data: {
                    permissionCodes: [
                      PermissionEnum.Phead,
                      PermissionEnum.PAdmin,
                      PermissionEnum.DAdmin,
                    ],
                  },
                },
              ],
              data: {
                permissionCodes: [
                  PermissionEnum.ReportList,
                  PermissionEnum.PAdmin,
                  PermissionEnum.Phead,
                  PermissionEnum.DAdmin,
                ],
              },
            },
            //{
            //  order: 21,
            //  name: 'menu.system-code',
            //  title: 'menu.system-code',
            //  icon: '',
            //  img: 'assets/images/systemcode.png',

            //  cssClass: '',
            //  link: '/system-code',
            //  childs: [],
            //  data: { permissionCodes: [PermissionEnum.SystemCode] },
            //},
            //{
            //  order: 22,
            //  name: 'menu.usersManagement',
            //  title: 'menu.usersManagement',
            //  icon: '',
            //  img: 'assets/images/usermanement.png',
            //  cssClass: '',
            //  childs: [
            //    {
            //      order: 1,
            //      link: `/user-management`,
            //      name: 'menu.usersList',
            //      title: 'menu.usersList',
            //      icon: '',
            //      img: '../../../../../../assets/media/sidebar/users.svg',
            //      cssClass: '',
            //      childs: [],
            //      data: { permissionCodes: [PermissionEnum.UserManagement] },
            //    },
            //    {
            //      order: 2,
            //      link: `/user-management/roles/list`,
            //      name: 'menu.rolesList',
            //      title: 'menu.rolesList',
            //      icon: '',
            //      img: '../../../../../../assets/media/sidebar/roles.svg',
            //      cssClass: '',
            //      childs: [],
            //      data: { permissionCodes: [PermissionEnum.UserManagement] },
            //    },
            //  ],
            //  data: {
            //    permissionCodes: [PermissionEnum.UserManagement],
            //  },
            //},
          ];

          // Filter navigation based on user permissions (AFTER token is exchanged)
          const filteredNavList = this.filterNavItems(allNavItems);

          // Emit at once without the AI configuration entry so the menu never waits on the
          // privilege round-trip, then emit again if the API grants it.
          sub.next(filteredNavList);

          this.hasAiConfigPrivilege().then(canManageAiConfig => {
            if (canManageAiConfig) {
              this.addAiConfigItem(filteredNavList);
              sub.next([...filteredNavList]);
            }
            sub.complete();
          });
        }
      );
    });
  }

  getUserNavItems(): Observable<NavItemModel[]> {
    // Initialize the navigation list
    this.navList = [
      {
        order: 1,
        name: 'menu.system-code',
        title: 'menu.system-code',
        icon: '',
        svg: 'settings',
        cssClass: '',
        link: '/system-code',
        childs: [],
        // Uncomment and define data if needed
        // data: { permissionCodes: [PermissionEnum.ProjectTaskList] },
      },
    ];

    // Optionally filter the navigation list based on permissions
    const filteredNavList = this.filterNavItem(this.navList);

    return new Observable(sub => {
      // Push filtered nav list and complete
      sub.next(filteredNavList);
      sub.complete();
    });
  }

  // Example: Filter navigation items based on permissions
  private filterNavItem(navList: NavItemModel[]): NavItemModel[] {
    // Implement permission filtering logic
    // Example: Return all items for now
    return navList.filter(item => {
      // Replace the condition with actual permission logic
      return true; // Allow all items
    });
  }

  navList: NavItemModel[] = [];
  // userNavList: NavItemModel[] = [];

  /**
   * Resolves the AiAssistant.ManageConfiguration privilege
   * (C9A7E5B2-3D18-4F60-B4E2-7C81F0A9D532).
   *
   * Privilege GUIDs are not carried in the JWT — its `permissions` claim holds integer
   * permission codes — so `PermissionEnum` cannot express this one and the API is the only
   * authority. Api/AiAdmin/ProviderTypes is decorated with
   * [RequireAiPrivilege(AiAssistant.ManageConfiguration)] and answers 403 without it, so a
   * successful response is the grant. Any failure resolves false: default-deny.
   */
  private hasAiConfigPrivilege(): Promise<boolean> {
    const userId = this.currentUserId();

    if (!this.aiConfigPrivilege || this.aiConfigPrivilegeUserId !== userId) {
      this.aiConfigPrivilegeUserId = userId;
      this.aiConfigPrivilege = firstValueFrom(
        this.aiAdminService.getProviderTypes()
      ).then(
        () => true,
        () => false
      );
    }
    return this.aiConfigPrivilege;
  }

  /**
   * The JWT subject of the signed-in user — the same value the API reads to decide privileges —
   * or null when it cannot be read. Used only to scope the privilege cache; a null answer simply
   * forces the question to be asked again.
   */
  private currentUserId(): string | null {
    try {
      return this.currentUserService.getCurrentUserIdString();
    } catch {
      return null;
    }
  }

  /**
   * Adds "Manage AI Config" under Administration. Called only when the API has confirmed the
   * AiAssistant.ManageConfiguration privilege. If Administration itself was filtered out — it
   * requires the Project Head permission — the parent is created so the screen stays reachable
   * for whoever holds the AI privilege.
   */
  private addAiConfigItem(navList: NavItemModel[]): void {
    const aiConfigItem: NavItemModel = {
      order: 6,
      link: '/ai-assistant/admin',
      name: 'menu.manageAiConfig',
      title: 'menu.manageAiConfig',
      cssClass: '',
      childs: [],
    };

    // The item is declared with its translation key, not its English label, so the lookup has to
    // use the key as well. Matching on 'Administration' never hit and a second, untranslated
    // parent was appended next to the real one on every menu build.
    let administration = navList.find(
      item => item.name === ADMINISTRATION_MENU_KEY
    );

    if (!administration) {
      administration = {
        order: 16,
        name: ADMINISTRATION_MENU_KEY,
        link: undefined,
        img: 'assets/images/systemcode.png',
        title: ADMINISTRATION_MENU_KEY,
        cssClass: '',
        childs: [],
      };
      navList.push(administration);
    }

    if (!administration.childs.some(child => child.link === aiConfigItem.link)) {
      administration.childs.push(aiConfigItem);
    }
  }
}
